import type { FC } from "react";
import { useMemo } from "react";
import { useNavigate } from "react-router";

import type { OrderAttributesFieldValues } from "@jti/commerce-types/types";
import { toast } from "@jti/core-components/components/Toaster";
import { newrelic } from "@jti/fe-clients";
import { customPaymentMethods, SinglePageCheckoutRoutes } from "@jti/fe-common";
import type { ApiMagentoOrderAttributeInput } from "@jti/magento-types";
import { useDispatch, useSelector } from "@jti/store/reduxHooks";
import { fetchCart } from "@jti/store/slices/cartSlice";
import { CheckoutV2LoyaltyChoice, checkoutV2Slice } from "@jti/store/slices/checkoutV2Slice";
import { useGetCustomerCartQuery, useGetOrderAttributesQuery } from "@jti/store/slices/magentoApiSlice";
import { useGetProfileQuery } from "@jti/store/slices/profileApiSlice";
import { deleteAnonymousCart } from "@jti/store/utils";
import { useTranslations } from "@jti/ui";

import { useCheckoutFastFlowContext } from "@checkout-v2/features/checkoutFastFlow";
import { normalizeProfileExtraAttributes } from "@checkout-v2/features/OrderAttributes/utils/normalizeProfileExtraAttributes";
import type { SubmitHandler } from "@checkout-v2/forms";
import { useCartItems, useGtmCtaClickPayment, useOrderLimit } from "@checkout-v2/shared/hooks";
import { useCartFreeOfCharge } from "@checkout-v2/shared/hooks/useCartFreeOfCharge";
import { usePlaceOrder } from "@checkout-v2/shared/hooks/usePlaceOrder/usePlaceOrder";
import { useCheckoutV2Configuration } from "@checkout-v2/shared/providers/CheckoutV2ConfigurationProvider";
import { saveOrderCheckoutFlow } from "@checkout-v2/shared/utils/purchaseTrackingStorage";

import type {
  ConsentAndPayFormFields,
  ConsentAndPayFormFieldsConfig,
  ConsentAndPayFormFieldsDefaultValues,
} from "../../ConsentAndPayForm.types";
import { consentFieldFactory } from "../../factories";
import { ConsentAndPayFormRenderer } from "../../renderer";
import { getPlaceOrderError } from "../../utils/getPlaceOrderError";
import { prepareMagentoAdditionalAttributes } from "../../utils/prepareMagentoAdditionalAttributes";

export const CartConsentAndPayFormOrchestrator: FC = () => {
  const { __ } = useTranslations();
  const checkoutConfig = useCheckoutV2Configuration();
  const { placeOrder } = usePlaceOrder();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isOrderLimitExceeded } = useOrderLimit();
  const { data: cartItems } = useCartItems();
  const { checkoutFlowType } = useCheckoutFastFlowContext();
  const { trackGtmCtaClickPayment } = useGtmCtaClickPayment();

  const checkoutData = useSelector((state) => state.checkoutV2.data);
  const storeGroupCode = useSelector((state) => state.cart.storeConfig?.store_group_code);
  const { data: customerCart } = useGetCustomerCartQuery();
  const { data: profileData } = useGetProfileQuery();
  const { data: definedOrderAttributes } = useGetOrderAttributesQuery();
  const { loyaltyChoice, loyaltyCampaignId } = useSelector((state) => state.checkoutV2.loyaltyPointsState);
  const { isCartFreeOfCharge } = useCartFreeOfCharge();

  const isOrderPagePaymentFlowEnabled = checkoutConfig.paymentFlow?.isOrderPagePaymentFlowEnabled;
  const orderWarningMessage = checkoutConfig.paymentFlow?.orderWarningMessage;

  const selectedPaymentMethodCode = checkoutData?.paymentMethodCode;
  const isPayOnDelivery = selectedPaymentMethodCode === customPaymentMethods.payOnDelivery;
  const isConsentEnabled = isPayOnDelivery || !checkoutConfig.general.policyAndTermsConsentDisabled;

  const formFieldsConfig: ConsentAndPayFormFieldsConfig = useMemo(() => {
    return {
      consent: consentFieldFactory.getDerivedConfig({ __, isConsentEnabled }),
    };
  }, [__, isConsentEnabled]);

  const formFieldsDefaultValues: ConsentAndPayFormFieldsDefaultValues = useMemo(() => {
    return {
      orderAttributes: checkoutData?.orderAttributes ?? {},
      consent: consentFieldFactory.getDefaultValue({}),
    };
  }, [checkoutData?.orderAttributes]);

  const profileOrderAttributes: OrderAttributesFieldValues = useMemo(() => {
    const normalizedProfileExtras = normalizeProfileExtraAttributes(profileData?.extra ?? {});
    const definedCodes = new Set((definedOrderAttributes ?? []).map((attr) => attr.code));

    return Object.fromEntries(Object.entries(normalizedProfileExtras).filter(([code]) => definedCodes.has(code)));
  }, [profileData?.extra, definedOrderAttributes]);

  const prepareAdditionalAttributes = (data: ConsentAndPayFormFields): ApiMagentoOrderAttributeInput[] => {
    const allOrderAttributes: OrderAttributesFieldValues = {
      ...profileOrderAttributes,
      ...checkoutData?.orderAttributes,
      ...data.orderAttributes,
    };

    const preparedAttributes = prepareMagentoAdditionalAttributes(allOrderAttributes);

    const cartAdditionalAttributes = customerCart?.customerCart?.additional_attributes ?? [];

    if (cartAdditionalAttributes.length) {
      const tradeInSkuAttr = cartAdditionalAttributes.find((attr) => attr?.attribute_code === "traded_in_new_sku");
      const isTradeInProductInCart =
        tradeInSkuAttr?.value && cartItems?.some((item) => item.sku === tradeInSkuAttr.value);

      if (isTradeInProductInCart) {
        const tradeInAttributes: ApiMagentoOrderAttributeInput[] = cartAdditionalAttributes
          .filter((attr): attr is NonNullable<typeof attr> => Boolean(attr?.value))
          .map(({ attribute_code, value }) => ({ attribute_code, value }));

        return [...preparedAttributes, ...tradeInAttributes];
      }
    }

    return preparedAttributes;
  };

  const handlerOrderPlacement = async (additionalAttributes?: ApiMagentoOrderAttributeInput[]) => {
    try {
      const result = await placeOrder(additionalAttributes);

      if (!result) {
        throw new Error("Order placement failed: Missing order data in the response.");
      }

      return result;
    } catch (error) {
      newrelic.noticeError(error as Error);

      const placeOrderError = getPlaceOrderError(error);

      if (placeOrderError) {
        toast.error(placeOrderError.message);
        throw placeOrderError;
      }

      toast.error(__("cart.checkout.payment_form.error.unknown_place_order_error"));

      throw error;
    }
  };

  const redirectToThankYouPage = (orderNumber: string) => {
    const thankYouPagePath = SinglePageCheckoutRoutes.getThankYouPagePath(orderNumber);

    navigate(thankYouPagePath);
  };

  const onSubmit: SubmitHandler<ConsentAndPayFormFields> = async (data, _form) => {
    const orderAttributes: ApiMagentoOrderAttributeInput[] = prepareAdditionalAttributes(data);
    const isRedeemWithoutVoucher = loyaltyChoice === CheckoutV2LoyaltyChoice.REDEEM && !loyaltyCampaignId;

    if (isRedeemWithoutVoucher) {
      dispatch(checkoutV2Slice.actions.setLoyaltyError(true));
      return;
    }

    trackGtmCtaClickPayment();

    let placeOrderResult: Awaited<ReturnType<typeof handlerOrderPlacement>>;

    try {
      placeOrderResult = await handlerOrderPlacement(orderAttributes);
    } catch {
      return;
    }

    saveOrderCheckoutFlow(placeOrderResult.number, checkoutFlowType ?? "standard_flow");

    // The placed cart is now consumed, so drop the persisted anonymous cart id to prevent a stale
    // getCartId lookup when the header mini cart later refetches the cart on the thank-you page.
    deleteAnonymousCart(storeGroupCode ?? "");

    // Refresh cart after order placement as cart is still using old checkout slice and cannot be invalidated automatically
    dispatch(fetchCart({}));

    if (isPayOnDelivery || isCartFreeOfCharge) {
      redirectToThankYouPage(placeOrderResult.number);
      return;
    }

    const orderPagePath = SinglePageCheckoutRoutes.getOrderRootRoute(placeOrderResult.number);

    navigate(orderPagePath, {
      state: { paymentMethodPreselected: true },
    });
  };

  return (
    <ConsentAndPayFormRenderer
      formFieldsConfig={formFieldsConfig}
      defaultValues={formFieldsDefaultValues}
      onSubmit={onSubmit}
      isPayOnDelivery={isPayOnDelivery}
      isConsentEnabled={isConsentEnabled}
      isFreeOfCharge={!!isCartFreeOfCharge}
      isSubmitButtonDisabled={isOrderLimitExceeded}
      isPaymentMethodPreselected={false}
      isOrderPagePaymentFlowEnabled={isOrderPagePaymentFlowEnabled}
      orderWarningMessage={orderWarningMessage}
    />
  );
};
